import { BlobServiceClient } from "@azure/storage-blob";
import Swal from "sweetalert2";
import optimizePhoto from "utils/util";
import {
  blob_azure_container,
  blob_azure_container_guidlines,
  sasToken,
  storageAccountName,
} from "./azure_credentials";

export const uploadReportPDFtoAzure = async (blob, userID, reportType = "") => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient =
      blobService.getContainerClient(blob_azure_container);

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }

    const blobClient = containerClient.getBlockBlobClient(
      `pdf_users/${userID}/${reportType}${userID}.pdf`
    );

    await blobClient.uploadBrowserData(blob);
  } catch (err) {
    console.log("ERROR AL SUBIR PDF.", err);
  }
};

export const saveReportPDFtoAzure = async (blob, userID, filename, reportType = "") => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient =
      blobService.getContainerClient(blob_azure_container);

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }

    const blobClient = containerClient.getBlockBlobClient(
      `pdf_users/${userID}/${reportType}${filename}.pdf`
    );

    await blobClient.uploadBrowserData(blob);
  } catch (err) {
    console.log("ERROR AL SUBIR PDF.", err);
  }
};

/**
 * Uploads every file to `<projectID>/<file.name>` in the photos container and WAITS
 * for all uploads to settle. It never throws and never shows UI: callers decide what
 * to do with the failures (e.g. skip the DB rows of photos whose blob was not stored).
 *
 * @param {File[]} arrayFile
 * @param {string|number} projectID
 * @returns {Promise<string[]>} names of the files that could NOT be uploaded ([] = all ok)
 */
export const uploadImageAzure = async (arrayFile = [], projectID) => {
  if (!arrayFile || arrayFile.length < 1) return [];

  let containerClient;
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    containerClient = blobService.getContainerClient(blob_azure_container);

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }
  } catch (err) {
    console.log(err);
    // Container unreachable: nothing was uploaded.
    return arrayFile.map((file) => file.name);
  }

  const results = await Promise.allSettled(
    arrayFile.map(async (file) => {
      const blobClient = containerClient.getBlockBlobClient(
        `${projectID}/${file.name}`
      );
      await blobClient.uploadBrowserData(file, {
        blobHTTPHeaders: { blobContentType: file.type },
      });
    })
  );

  const failedNames = [];
  results.forEach((result, idx) => {
    if (result.status === "rejected") {
      console.log("Azure upload failed:", arrayFile[idx].name, result.reason);
      failedNames.push(arrayFile[idx].name);
    }
  });
  return failedNames;
};

export const updateImageAzure = async (file, projectID) => {
  const blobService = new BlobServiceClient(
    `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
  );

  const containerClient =
    blobService.getContainerClient(blob_azure_container);

  if ((await containerClient.exists()) === false) {
    await containerClient.createIfNotExists({
      access: "container",
    });
  }

  const blobClient = containerClient.getBlockBlobClient(
    `${projectID}/${file.name}`
  );

  const options = {
    blobHTTPHeaders: { blobContentType: file.type },
  };

  // Throws on failure so callers can decide whether to proceed with DB writes.
  await blobClient.uploadBrowserData(file, options);
};

/**
 * Moves a blob from sourcePath to destPath within blob_azure_container.
 * Used to relocate a cover image from a temporary path to its final project folder
 * after the project ID is known.
 *
 * The "move" is implemented as copy-then-delete because the Azure SDK for
 * browsers does not support a native rename operation.
 *
 * @param {string} sourcePath - e.g. "temp_covers/uuid.jpg"
 * @param {string} destPath   - e.g. "projectId/uuid.jpg"
 * @returns {Promise<void>} Throws on failure.
 */
export const moveBlobAzure = async (sourcePath, destPath) => {
  const blobService = new BlobServiceClient(
    `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
  );

  const containerClient = blobService.getContainerClient(blob_azure_container);

  const sourceClient = containerClient.getBlobClient(sourcePath);
  const destClient = containerClient.getBlobClient(destPath);

  const copyPoller = await destClient.beginCopyFromURL(sourceClient.url);
  await copyPoller.pollUntilDone();

  // Remove the temporary source blob after a successful copy.
  await sourceClient.deleteIfExists();
};

export const downloadImageAzure = async (name) => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );
    const containerClient = blobService.getContainerClient(
      blob_azure_container_guidlines
    );

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }
    console.log(
      name
        .split("/")
        .map((i) => i)
        .join("/")
    );
    const blobClient = containerClient.getBlockBlobClient(
      name
        .split("/")
        .map((i) => i)
        .join("/") /* `${projectID}/${file.name}`*/
    );

    const blobResponse = await blobClient.download();

    return await blobResponse.blobBody;
  } catch (err) {
    console.log(err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: `Can't download image`,
    });
  }
};

export const uploadOptimizeImageAzure = async (file, adminId, size) => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient = blobService.getContainerClient(
      blob_azure_container_guidlines
    );

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }

    const blobClient = containerClient.getBlockBlobClient(
      `${adminId}/${file.name}`
    );

    const options = {
      blobHTTPHeaders: { blobContentType: file.type },
    };
    const optimizeFile = await optimizePhoto(file, size);
    await blobClient.uploadBrowserData(optimizeFile, options);
  } catch (err) {
    console.log(err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: `Image could not be saved. There are problems with Azure`,
    });
  }
};

export const updateGuidlinesImageAzure = async (file, adminId) => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient = blobService.getContainerClient(
      blob_azure_container_guidlines
    );

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }

    const blobClient = containerClient.getBlockBlobClient(
      `${adminId}/${file.name}`
    );

    const options = {
      blobHTTPHeaders: { blobContentType: file.type },
    };

    await blobClient.uploadBrowserData(file, options);
  } catch (err) {
    console.log(err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: `Image could not be saved. There are problems with Azure`,
    });
  }
};

export const updateImageAzureWithThumbnails = async (file, projectID) => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient = blobService.getContainerClient(blob_azure_container);
    const containerCropedClient = blobService.getContainerClient(
      `${blob_azure_container}croped`
    );

    if ((await containerClient.exists()) === false) {
      await containerClient.createIfNotExists({
        access: "container",
      });
    }

    if ((await containerCropedClient.exists()) === false) {
      await containerCropedClient.createIfNotExists({
        access: "container",
      });
    }

    const baseName = file.name.split(".")[0];
    const extension = file.name.split(".").pop();

    // 1. Upload original
    const originalBlobClient = containerClient.getBlockBlobClient(
      `${projectID}/${file.name}`
    );
    const originalOptions = { blobHTTPHeaders: { blobContentType: file.type } };
    const uploadOriginal = originalBlobClient.uploadBrowserData(
      file,
      originalOptions
    );

    // 2. Generate and upload Medium
    const mediumFile = await optimizePhoto(file, 800);
    const mediumFileName = `${projectID}/${baseName}-medium.jpg`;
    const mediumBlobClient =
      containerCropedClient.getBlockBlobClient(mediumFileName);
    const mediumOptions = { blobHTTPHeaders: { blobContentType: "image/jpeg" } };
    const uploadMedium = mediumBlobClient.uploadBrowserData(
      mediumFile,
      mediumOptions
    );

    // 3. Generate and upload Thumbnail
    const thumbnailFile = await optimizePhoto(file, 200);
    const thumbnailFileName = `${projectID}/${baseName}-thumbnail.jpg`;
    const thumbnailBlobClient =
      containerCropedClient.getBlockBlobClient(thumbnailFileName);
    const thumbnailOptions = {
      blobHTTPHeaders: { blobContentType: "image/jpeg" },
    };
    const uploadThumbnail = thumbnailBlobClient.uploadBrowserData(
      thumbnailFile,
      thumbnailOptions
    );

    await Promise.all([uploadOriginal, uploadMedium, uploadThumbnail]);
  } catch (err) {
    console.log(err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: `Image and thumbnails could not be saved. There are problems with Azure`,
    });
  }
};

export const cloneAzureImage = async (array = [], projectId) => {
  try {
    const blobService = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient =
      blobService.getContainerClient(blob_azure_container);

    const destinationPrefix = `${projectId}/`;
    const containerCropedClient = blobService.getContainerClient(`${blob_azure_container}croped`);

    const copyPromises = [];

    for (const image of array) {
      // Never use a cache-busting query as part of a blob name.
      const id = String(image.id).split("?")[0];
      const newUrl = String(image.newUrl).split("?")[0];

      const imageName = `${projectId}/${id}`;
      const sourceBlobClient = containerClient.getBlobClient(imageName);
      const destinationBlobName = destinationPrefix + newUrl;

      const destinationBlobClient =
        containerClient.getBlobClient(destinationBlobName);
      copyPromises.push(
        destinationBlobClient.beginCopyFromURL(sourceBlobClient.url)
      );

      // Clone thumbnail as well
      const thumbnailName = `${projectId}/${id.split(".")[0]}-thumbnail.jpg`;
      const sourceThumbnailClient = containerCropedClient.getBlobClient(thumbnailName);
      const destinationThumbnailName = `${projectId}/${newUrl.split(".")[0]}-thumbnail.jpg`;
      const destinationThumbnailClient = containerCropedClient.getBlobClient(destinationThumbnailName);

      copyPromises.push(
        destinationThumbnailClient.beginCopyFromURL(sourceThumbnailClient.url)
      );
    }

    // Wait for the copies so failures are reported. Copy order in `copyPromises` is
    // [original, thumbnail] per image; a missing thumbnail is only logged.
    const results = await Promise.allSettled(copyPromises);
    let originalFailed = false;
    results.forEach((result, idx) => {
      if (result.status === "rejected") {
        console.error("Error copying blob:", result.reason);
        if (idx % 2 === 0) originalFailed = true;
      }
    });
    if (originalFailed) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Some images could not be copied. There are problems with Azure`,
      });
    }
  } catch (err) {
    console.log(err);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: `Image could not be saved. There are problems with Azure`,
    });
  }
};
